import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type KeyboardEvent, type ReactElement } from "react";
import {
  ConfirmDialog,
  Field,
  Modal,
  MoneyInput,
  QtyInput,
  QueryStatus,
  StatusBadge,
  useToast,
} from "@cx/ui";
import type {
  ListResult,
  ProductLotsView,
  StockBalanceRow,
  StockBalanceView,
  StockMovementKind,
  StockMovementView,
  WarehouseView,
} from "@cx/contracts";
import { formatBRL, formatDateBR, formatQty } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import {
  LOT_STATUS_LABELS,
  LOT_STATUS_TONES,
  emptyLotDraft,
  lotDraftProblem,
  lotPayload,
  lotTakesPayload,
  lotTakesProblem,
  movementLabel,
  movementLotsText,
  newIdempotencyKey,
  signedQty,
} from "./labels";
import { LotFields, LotTakesFields } from "./LotFields";

const KIND_LABEL: Record<StockMovementKind, string> = { E: "Entrada", S: "Saída", B: "Balanço" };
const REVERSIBLE = new Set(["manual", "transfer"]);
const qty = (q: number | null) => (q === null ? "—" : formatQty(q));

/** Chaves de cache do estoque de um produto (a tela de produto e a de estoque compartilham). */
export const stockKeys = (companyId: string, productId: string) => ({
  balances: ["stock-balances", companyId, productId],
  movements: ["stock-movements", companyId, productId],
  lots: ["stock-lots", companyId, productId],
});

/**
 * Estoque de um produto (RF-SUP-01): saldos por depósito com o total dos depósitos que contam no saldo, extrato com o
 * saldo do depósito depois de cada movimento, lançamento (entrada, saída, balanço), transferência e estorno.
 */
export function StockPanel({ productId }: { productId: string }): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const canMove = can(perms, "stock", "create");
  const canSeeCost = can(perms, "products", "read_cost");
  const keys = stockKeys(companyId, productId);
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [dialog, setDialog] = useState<"move" | "transfer" | null>(null);
  const [reversing, setReversing] = useState<StockMovementView | null>(null);

  const warehouses = useQuery({
    queryKey: ["warehouses", companyId],
    queryFn: () => api<{ data: WarehouseView[] }>("/warehouses"),
  });
  // A leitura de saldos não cria o depósito padrão: espera a lista de depósitos (que cria o "Geral" da empresa nova).
  const balances = useQuery({
    queryKey: keys.balances,
    enabled: !warehouses.isPending,
    queryFn: () => api<StockBalanceView>(`/stock/balances?productId=${productId}`),
  });
  const ownStock = balances.data?.derived === null && balances.data.stockControl;
  const showStatement = !!balances.data && balances.data.derived !== "kit";
  // Lotes (T3.2): só do produto com saldo próprio; a resposta diz se ele controla lotes.
  const lots = useQuery({
    queryKey: keys.lots,
    enabled: ownStock,
    queryFn: () => api<ProductLotsView>(`/stock/product-lots?productId=${productId}`),
  });
  const batch = ownStock && lots.data?.batchControl === true ? lots.data : undefined;
  const movements = useInfiniteQuery({
    queryKey: [...keys.movements, warehouseFilter],
    enabled: showStatement,
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ productId, limit: "20" });
      if (warehouseFilter) qs.set("warehouseId", warehouseFilter);
      if (pageParam) qs.set("cursor", pageParam);
      return api<ListResult<StockMovementView>>(`/stock/movements?${qs}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: keys.balances });
    qc.invalidateQueries({ queryKey: keys.movements });
    qc.invalidateQueries({ queryKey: keys.lots });
    // Custo médio e disponível de kits mudam com o saldo.
    qc.invalidateQueries({ queryKey: ["product", companyId] });
  };
  const reverse = useMutation({
    mutationFn: (m: { id: string; key: string }) =>
      api(`/stock/movements/${m.id}/reverse`, {
        method: "POST",
        body: JSON.stringify({}),
        headers: { "Idempotency-Key": m.key },
      }),
    onSuccess: () => {
      toast("success", "Movimento estornado");
      refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });

  const b = balances.data;
  const activeWarehouses = (warehouses.data?.data ?? []).filter((w) => w.active);
  const rows = movements.data?.pages.flatMap((p) => p.data) ?? [];
  const showProduct = b?.derived === "variations";
  const valueCell = (r: Pick<StockBalanceRow, "valueCents">) =>
    canSeeCost && b?.derived !== "kit" ? (
      <td className="text-end money">{r.valueCents == null ? "—" : formatBRL(r.valueCents)}</td>
    ) : null;

  return (
    <div>
      <QueryStatus loading={warehouses.isPending || balances.isLoading} error={balances.error} />
      {b && !b.stockControl && b.derived === null && (
        <p className="text-muted">Este produto não controla estoque (veja “Controlar estoque” acima).</p>
      )}
      {b && (b.stockControl || b.derived !== null) && (
        <>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <h2 className="h6 mb-0 flex-grow-1">Saldos por depósito</h2>
            {ownStock && canMove && (
              <>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => setDialog("move")}>
                  Lançar estoque
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={activeWarehouses.length < 2}
                  onClick={() => setDialog("transfer")}
                >
                  Transferir entre depósitos
                </button>
              </>
            )}
          </div>
          {b.derived === "kit" && (
            <p className="text-muted small">
              Kit não tem saldo próprio: o disponível é calculado pelos componentes em cada depósito.
            </p>
          )}
          {b.derived === "variations" && (
            <p className="text-muted small">Soma das variações. Lance o estoque em cada variação.</p>
          )}
          <div className="table-responsive mb-3">
            <table className="table align-middle" aria-label="Saldos por depósito">
              <thead>
                <tr>
                  <th scope="col">Depósito</th>
                  {b.derived !== "kit" && (
                    <>
                      <th scope="col" className="text-end">
                        Físico
                      </th>
                      <th scope="col" className="text-end">
                        Reservado
                      </th>
                    </>
                  )}
                  <th scope="col" className="text-end">
                    Disponível
                  </th>
                  {canSeeCost && b.derived !== "kit" && (
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r) => (
                  <tr key={r.warehouseId}>
                    <td>
                      {r.warehouseName}
                      {r.isDefault && <span className="badge text-bg-light border ms-2">Padrão</span>}
                      {!r.countsInTotal && <span className="text-muted small ms-2">(fora do total)</span>}
                      {!r.active && <span className="text-muted small ms-2">(inativo)</span>}
                    </td>
                    {b.derived !== "kit" && (
                      <>
                        <td className="text-end qty">{qty(r.physicalQ4)}</td>
                        <td className="text-end qty">{qty(r.reservedQ4)}</td>
                      </>
                    )}
                    <td className="text-end qty">
                      {r.availableQ4 === null ? "Sem limite" : qty(r.availableQ4)}
                    </td>
                    {valueCell(r)}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="fw-semibold">
                  <th scope="row">Total</th>
                  {b.derived !== "kit" && (
                    <>
                      <td className="text-end qty" data-testid="stock-total-physical">
                        {qty(b.total.physicalQ4)}
                      </td>
                      <td className="text-end qty">{qty(b.total.reservedQ4)}</td>
                    </>
                  )}
                  <td className="text-end qty" data-testid="stock-total-available">
                    {b.total.availableQ4 === null ? "Sem limite" : qty(b.total.availableQ4)}
                  </td>
                  {valueCell(b.total)}
                </tr>
              </tfoot>
            </table>
          </div>
          {canSeeCost && b.avgCostCents !== undefined && (
            <p className="mb-3">
              Custo médio: <strong data-testid="stock-avg-cost">{formatBRL(b.avgCostCents)}</strong>
            </p>
          )}
          {batch && <LotsTable lots={batch} />}
        </>
      )}

      {showStatement && b && (b.stockControl || b.derived !== null) && (
        <>
          <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
            <h2 className="h6 mb-0 flex-grow-1">Extrato de movimentos</h2>
            <div>
              <label className="form-label small mb-1" htmlFor={`stock-filter-${productId}`}>
                Depósito do extrato
              </label>
              <select
                id={`stock-filter-${productId}`}
                className="form-select form-select-sm"
                value={warehouseFilter}
                onChange={(e) => setWarehouseFilter(e.target.value)}
              >
                <option value="">Todos</option>
                {(warehouses.data?.data ?? []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <QueryStatus loading={movements.isLoading} error={movements.error} />
          <div className="table-responsive">
            <table className="table table-sm align-middle" aria-label="Extrato de movimentos">
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  {showProduct && <th scope="col">Variação</th>}
                  <th scope="col">Depósito</th>
                  <th scope="col">Movimento</th>
                  <th scope="col" className="text-end">
                    Quantidade
                  </th>
                  <th scope="col" className="text-end">
                    Saldo no depósito
                  </th>
                  {canSeeCost && (
                    <th scope="col" className="text-end">
                      Custo unitário
                    </th>
                  )}
                  <th scope="col">Observação</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {movements.isSuccess && rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-muted py-3">
                      Nenhum movimento.
                    </td>
                  </tr>
                )}
                {rows.map((m) => (
                  <tr key={m.id}>
                    <td className="text-nowrap">{dateTime(m.occurredAt)}</td>
                    {showProduct && <td>{m.productName}</td>}
                    <td>{m.warehouseName}</td>
                    <td>
                      {movementLabel(m)}
                      {m.kind === "B" && (
                        <span className="text-muted small ms-1">(contado {formatQty(m.qtyQ4)})</span>
                      )}
                      {m.reversedById && <span className="badge text-bg-light border ms-2">Estornado</span>}
                      {m.lots.length > 0 && (
                        <div className="text-muted small">Lotes: {movementLotsText(m.lots)}</div>
                      )}
                    </td>
                    <td className="text-end qty">{signedQty(m.deltaQ4)}</td>
                    <td className="text-end qty">{formatQty(m.balanceAfterQ4)}</td>
                    {canSeeCost && (
                      <td className="text-end money">
                        {m.unitCostCents === null || m.unitCostCents === undefined
                          ? "—"
                          : formatBRL(m.unitCostCents)}
                      </td>
                    )}
                    <td>{m.notes ?? ""}</td>
                    <td className="text-end">
                      {canMove &&
                        ownStock &&
                        !m.reversesId &&
                        !m.reversedById &&
                        m.deltaQ4 !== 0 &&
                        REVERSIBLE.has(m.originType ?? "") && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            aria-label={`Estornar movimento de ${dateTime(m.occurredAt)}`}
                            disabled={reverse.isPending}
                            onClick={() => setReversing(m)}
                          >
                            Estornar
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {movements.hasNextPage && (
            <div className="text-center">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={movements.isFetchingNextPage}
                onClick={() => movements.fetchNextPage()}
              >
                Carregar mais
              </button>
            </div>
          )}
        </>
      )}

      {dialog === "move" && (
        <MovementDialog
          productId={productId}
          warehouses={activeWarehouses}
          canSeeCost={canSeeCost}
          lots={batch}
          onClose={() => setDialog(null)}
          onDone={refresh}
        />
      )}
      {dialog === "transfer" && (
        <TransferDialog
          productId={productId}
          warehouses={activeWarehouses}
          lots={batch}
          onClose={() => setDialog(null)}
          onDone={refresh}
        />
      )}
      <ConfirmDialog
        open={!!reversing}
        title="Estornar movimento"
        message={
          reversing?.transferGroupId
            ? "O estorno desfaz os dois lados da transferência com novos movimentos. O original continua no extrato."
            : "O estorno lança o movimento inverso. O original continua no extrato."
        }
        confirmLabel="Estornar"
        onConfirm={() => {
          if (reversing) reverse.mutate({ id: reversing.id, key: newIdempotencyKey() });
          setReversing(null);
        }}
        onCancel={() => setReversing(null)}
      />
    </div>
  );
}

/**
 * O painel também aparece dentro do formulário do produto (FormPage é um <form>): Enter num campo do diálogo faria a
 * submissão implícita do produto. Aqui o Enter não submete; o diálogo salva pelo botão.
 */
const noImplicitSubmit = (e: KeyboardEvent<HTMLDivElement>) => {
  if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
};

type DialogProps = {
  productId: string;
  warehouses: WarehouseView[];
  /** Lotes do produto, quando ele controla lotes. */
  lots: ProductLotsView | undefined;
  onClose(): void;
  onDone(): void;
};

/** Erros de campo da API (400) e de regra (422) no formulário do diálogo. */
function useStockPost<T>(path: string, onSuccess: () => void) {
  const { api } = useSession();
  const toast = useToast();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(newIdempotencyKey);
  const m = useMutation({
    mutationFn: (body: T) =>
      api(path, { method: "POST", body: JSON.stringify(body), headers: { "Idempotency-Key": key } }),
    onSuccess,
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  return { m, errors, setErrors };
}

function MovementDialog({
  productId,
  warehouses,
  canSeeCost,
  lots,
  onClose,
  onDone,
}: DialogProps & { canSeeCost: boolean }) {
  const toast = useToast();
  const [kind, setKind] = useState<StockMovementKind>("E");
  const [warehouseId, setWarehouseId] = useState(
    warehouses.find((w) => w.isDefault)?.id ?? warehouses[0]?.id ?? "",
  );
  const [qtyQ4, setQty] = useState<number | null>(null);
  const [cost, setCost] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [lot, setLot] = useState(emptyLotDraft);
  const [takes, setTakes] = useState<Record<string, number | null>>({});
  const [open, setOpen] = useState(true);
  const { m, errors, setErrors } = useStockPost("/stock/movements", () => {
    toast("success", "Estoque lançado");
    onDone();
    setOpen(false);
  });
  const lotProblem = lots && kind === "E" ? lotDraftProblem(lot, true) : null;
  const chosen = lots && kind === "S" ? lotTakesPayload(takes) : null;
  const takesProblem = lotTakesProblem(chosen, qtyQ4);
  const [tried, setTried] = useState(false);
  const submit = () => {
    setTried(true);
    if (qtyQ4 === null) return setErrors({ qtyQ4: "Informe a quantidade" });
    if (lotProblem || takesProblem) return;
    m.mutate({
      productId,
      warehouseId,
      kind,
      qtyQ4,
      ...(kind !== "S" && canSeeCost && cost !== null ? { unitCostCents: cost } : {}),
      ...(lots && kind === "E" ? { lot: lotPayload(lot) } : {}),
      ...(chosen ? { lots: chosen } : {}),
      notes,
    });
  };
  return (
    <Modal
      open={open}
      title="Lançar estoque"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" disabled={m.isPending} onClick={submit}>
            Lançar
          </button>
        </>
      }
    >
      <div className="row" onKeyDown={noImplicitSubmit}>
        <Field label="Tipo" className="col-md-6" error={errors.kind}>
          <select
            className="form-select"
            value={kind}
            onChange={(e) => setKind(e.target.value as StockMovementKind)}
          >
            {(Object.keys(KIND_LABEL) as StockMovementKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Depósito" className="col-md-6" error={errors.warehouseId}>
          <select
            className="form-select"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={kind === "B" ? "Quantidade contada" : "Quantidade"}
          required
          className="col-md-6"
          error={errors.qtyQ4}
          hint={kind === "B" ? "O saldo do depósito passa a ser esta quantidade." : undefined}
        >
          <QtyInput value={qtyQ4} onChange={setQty} data-autofocus />
        </Field>
        {kind !== "S" && canSeeCost && (
          <Field
            label="Custo unitário"
            className="col-md-6"
            error={errors.unitCostCents}
            hint="Vazio: usa o preço de custo do cadastro."
          >
            <MoneyInput value={cost} onChange={setCost} />
          </Field>
        )}
        {lots && kind === "E" && (
          <div className="col-12">
            <LotFields value={lot} onChange={setLot} required error={tried ? lotProblem : null} />
          </div>
        )}
        {lots && kind === "S" && (
          <LotTakesFields
            lots={lots.warehouses.find((w) => w.warehouseId === warehouseId)}
            value={takes}
            onChange={setTakes}
            problem={tried ? takesProblem : null}
          />
        )}
        {lots && kind === "B" && (
          <p className="col-12 text-muted small">
            No balanço, a sobra fica sem lote e a falta sai dos lotes pela validade (o que vence antes).
          </p>
        )}
        <Field label="Observação" className="col-12" error={errors.notes}>
          <input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function TransferDialog({ productId, warehouses, lots, onClose, onDone }: DialogProps) {
  const toast = useToast();
  const def = warehouses.find((w) => w.isDefault) ?? warehouses[0];
  const [from, setFrom] = useState(def?.id ?? "");
  const [to, setTo] = useState(warehouses.find((w) => w.id !== def?.id)?.id ?? "");
  const [qtyQ4, setQty] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [takes, setTakes] = useState<Record<string, number | null>>({});
  const [tried, setTried] = useState(false);
  const chosen = lots ? lotTakesPayload(takes) : null;
  const takesProblem = lotTakesProblem(chosen, qtyQ4);
  const [open, setOpen] = useState(true);
  const { m, errors, setErrors } = useStockPost("/stock/transfers", () => {
    toast("success", "Transferência lançada");
    onDone();
    setOpen(false);
  });
  const select = (label: string, value: string, set: (v: string) => void, err?: string) => (
    <Field label={label} className="col-md-6" error={err}>
      <select className="form-select" value={value} onChange={(e) => set(e.target.value)}>
        {warehouses.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </select>
    </Field>
  );
  return (
    <Modal
      open={open}
      title="Transferir entre depósitos"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={m.isPending}
            onClick={() => {
              setTried(true);
              if (qtyQ4 === null) return setErrors({ qtyQ4: "Informe a quantidade" });
              if (takesProblem) return;
              m.mutate({
                productId,
                fromWarehouseId: from,
                toWarehouseId: to,
                qtyQ4,
                ...(chosen ? { lots: chosen } : {}),
                notes,
              });
            }}
          >
            Transferir
          </button>
        </>
      }
    >
      <div className="row" onKeyDown={noImplicitSubmit}>
        {select("Depósito de origem", from, setFrom, errors.fromWarehouseId)}
        {select("Depósito de destino", to, setTo, errors.toWarehouseId)}
        <Field label="Quantidade" required className="col-md-6" error={errors.qtyQ4}>
          <QtyInput value={qtyQ4} onChange={setQty} data-autofocus />
        </Field>
        {lots && (
          <LotTakesFields
            lots={lots.warehouses.find((w) => w.warehouseId === from)}
            value={takes}
            onChange={setTakes}
            problem={tried ? takesProblem : null}
          />
        )}
        <Field label="Observação" className="col-12" error={errors.notes}>
          <input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

/** Lotes por depósito (RF-SUP-07): saldo de cada lote com a validade e o que está "Sem lote". */
function LotsTable({ lots }: { lots: ProductLotsView }) {
  const rows = lots.warehouses.filter((w) => w.lots.some((l) => l.physicalQ4 !== 0) || w.semLoteQ4 !== 0);
  return (
    <>
      <h2 className="h6">Lotes</h2>
      {rows.length === 0 ? (
        <p className="text-muted small">Nenhum lote com saldo. Informe o lote em cada entrada.</p>
      ) : (
        <div
          className="table-responsive mb-3"
          tabIndex={0}
          role="region"
          aria-label="Lotes por depósito (rolagem)"
        >
          <table className="table table-sm align-middle" aria-label="Lotes por depósito">
            <thead>
              <tr>
                <th scope="col">Depósito</th>
                <th scope="col">Lote</th>
                <th scope="col">Fabricação</th>
                <th scope="col">Validade</th>
                <th scope="col" className="text-end">
                  Saldo
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.flatMap((w) => [
                ...w.lots
                  .filter((l) => l.physicalQ4 !== 0)
                  .map((l) => (
                    <tr key={`${w.warehouseId}-${l.lotId}`}>
                      <td>{w.warehouseName}</td>
                      <td>{l.code}</td>
                      <td>{l.manufacturedAt ? formatDateBR(l.manufacturedAt) : "—"}</td>
                      <td>
                        {l.expiresAt ? formatDateBR(l.expiresAt) : "—"}{" "}
                        {l.status !== "valid" && (
                          <StatusBadge tone={LOT_STATUS_TONES[l.status]}>
                            {LOT_STATUS_LABELS[l.status]}
                          </StatusBadge>
                        )}
                      </td>
                      <td className="text-end qty">{formatQty(l.physicalQ4)}</td>
                    </tr>
                  )),
                ...(w.semLoteQ4 !== 0
                  ? [
                      <tr key={`${w.warehouseId}-sem-lote`}>
                        <td>{w.warehouseName}</td>
                        <td className="text-muted">Sem lote</td>
                        <td>—</td>
                        <td>—</td>
                        <td className="text-end qty">{formatQty(w.semLoteQ4)}</td>
                      </tr>,
                    ]
                  : []),
              ])}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
